import { createStyles } from 'antd-style';
import { ospThemes } from '../ospThemes';
import { styleToCss } from '../utils/styleTocss';
import { type TableViewProps } from './types';

export const useStyles = createStyles(({ css, prefixCls }, { cellStyle, borderConfig, columnStyle }: Pick<TableViewProps<any>, "cellStyle" | "borderConfig" | "columnStyle">) => {
  const zoreBorderRadius = css`
    border-radius: 0px;
  `;

  const columnStyleCss = styleToCss(columnStyle || {})

  return {
    table: css`
      table {
        border-collapse: separate;
        background-color: ${cellStyle?.backgroundColor};
      }
      
      /* Table container styles */
      .${prefixCls}-table {
        border-radius: 0px 0px ${borderConfig?.borderRadiusSize}px ${borderConfig?.borderRadiusSize}px;
        scrollbar-width: thin; 
        -ms-overflow-style: thin;  
        scrollbar-color: ${ospThemes.colors.background_white_overlay10} transparent;
        font-family: initial;
        font-size: 0px;
        line-height: initial;

        .${prefixCls}-table-header {
          border-radius: 0px !important;

          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none;  /* Internet Explorer 10+ */
         
          &::-webkit-scrollbar {
            display: none;
          } 
        }

        .${prefixCls}-table-expanded-row-fixed {
          background-color: ${cellStyle?.backgroundColor};
          div[class*="${prefixCls}-empty-description"] {
            color: ${ospThemes.colors.content_tertiary};
          }
        }
        
        .${prefixCls}-table-sticky-scroll {
          border: none;
          display: none;
        }

        /* Header styles */
        &-thead {
          > tr > th {
            padding: 12px;
            border: none;
            background-color: ${ospThemes.colors.background_solid_secondary};
            color: ${ospThemes.colors.content_tertiary};
            border-bottom: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
            ${columnStyleCss}
            &::before {
              display: none;
            }
          }

          &::after {
            content: "";
            display: block;
            height: 12px;
            width: 100%;
            background: ${cellStyle?.backgroundColor};
          }
        }
        /* Body styles */
        &-tbody {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none;  /* Internet Explorer 10+ */
         
          &::-webkit-scrollbar {
            display: none;
          } 

          > tr {
            background-color: ${ospThemes.colors.background_solid_secondary};
            color: ${ospThemes.colors.content_tertiary};
            border-top: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
           &.${prefixCls}-table-placeholder {
              background: ${cellStyle?.backgroundColor} !important;
              height: 460px !important;
              &:hover {
               background: ${cellStyle?.backgroundColor} !important;
                > td{
                  background-color: transparent;
                }
              }
           }
            &:not(:last-child) {
              > td {
                border-top: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
                border-bottom: none;
              }
            }
            

            &:first-child {
              > td {
                border-top: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
                &:first-child {
                // 左上圆角
                  border-radius: ${borderConfig?.borderRadiusSize}px 0px 0px 0px;
                }

                &:last-child {
                // 右上圆角
                  border-radius: 0px ${borderConfig?.borderRadiusSize}px 0px 0px;
                }
              }
            }


            &:last-child {
              > td {
                border-top: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
                border-bottom: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
                &:first-child {
                // 左下圆角
                  border-radius: 0px ${borderConfig?.borderRadiusSize}px 0px 0px;
                }
                &:last-child {
                // 右下圆角 
                  border-radius: 0px ${borderConfig?.borderRadiusSize}px 0px 0px;
                }
              }  
            }

            > td {
              padding: 12px;
              &:first-child {
                background-color: ${cellStyle?.backgroundColor};
                border-left: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
              }

              &:last-child {
                border-right: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
              }
            }


            
          }

          .${prefixCls}-table-row {
              background-color: ${ospThemes.colors.background_solid_secondary};
              color: ${ospThemes.colors.content_tertiary};
              border-top: ${borderConfig?.borderWidth}px solid ${borderConfig?.borderColor};
            > .${prefixCls}-table-cell-row-hover {
              background-color: ${cellStyle?.backgroundColor};
            }
          }
          .${prefixCls}-table-cell-fix-left {
            background: ${cellStyle?.backgroundColor};
          }
          .${prefixCls}-table-cell-fix-right {
            background: ${cellStyle?.backgroundColor};
          }
        }
      }

      /* Reset border radius */
      .${prefixCls}-table-container table > thead > tr:first-child {
        > *:first-child,
        > *:last-child {
          ${zoreBorderRadius}
        }
      }
      .${prefixCls}-table-footer {
        background-color: ${ospThemes.colors.background_solid_secondary};
      }
      .${prefixCls}-table-pagination.${prefixCls}-pagination{
        margin: 16px 60px;
      }
        .ant-table-empty {
          background-color: ${ospThemes.colors.background_solid_secondary};
        }
      .${prefixCls}-pagination {
        color: ${ospThemes.colors.content_tertiary};
        background-color: ${ospThemes.colors.background_solid_secondary};
        display: flex;
        justify-content: flex-start;
        align-items: center;
        li:first-child {
          margin-right: auto;
        }
          
       > li {
        &.${prefixCls}-pagination-prev{
          background-color: transparent;
          border: none;
          >button {
            background-color: transparent;
            color:  ${ospThemes.colors.content_primary};
          }
          >button[disabled] {
            color: ${ospThemes.colors.content_quaternary};
          }
        }

        > a {
          .${prefixCls}-pagination-item-container{
            > span {
              color: ${ospThemes.colors.content_tertiary};

               &.${prefixCls}-pagination-item-ellipsis{
                color: ${ospThemes.colors.content_tertiary}; 
              }
              
              &.${prefixCls}-pagination-item-link-icon{
                color: ${ospThemes.colors.content_tertiary};
              }
            }
          }
        }
        
        &.${prefixCls}-pagination-next{
           >button {
            background-color: transparent;
            color:  ${ospThemes.colors.content_primary};
          }
          >button[disabled] {
            color: ${ospThemes.colors.content_quaternary};
          }
        }
        
        &.${prefixCls}-pagination-item{
          background-color: transparent;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 32px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        &.${prefixCls}-pagination-item-active{
          color: ${ospThemes.colors.content_primary};
          background-color: ${ospThemes.colors.background_white_overlay5};
          > a{
            color: ${ospThemes.colors.content_primary};
            &:hover {
              color: ${ospThemes.colors.content_primary};
            }
          }
        }

        > a {
          color: ${ospThemes.colors.content_tertiary};
          &:hover {
            color: ${ospThemes.colors.content_tertiary};
          }
        }
        &:hover {
          color: ${ospThemes.colors.content_tertiary};
        }
       }
        
      }

      .${prefixCls}-spin-dot-spin {
        color: #D3D3D3;
        display: none;
      }

      .${prefixCls}-spin-blur {
        background-color: ${ospThemes.colors.background_solid_secondary};
        opacity: 0;
        &::after{
          content: none;
        }
      }

      /* 自定义加载状态样式 */
      .${prefixCls}-spin-spinning {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        
      }

    `,
    loadingWrapper: css`
      background-color: ${ospThemes.colors.background_solid_secondary};
      position: relative !important;
      
      .${prefixCls}-spin {
        position: absolute !important;
        inset: 0 !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        z-index: 1 !important;
      }
      
      .${prefixCls}-spin-container {
        display: block !important;
      }
    `,

  };
});

export const useCssStyle = <T>(options: Pick<TableViewProps<T>, "columnStyle" | "cellStyle" | "spaceStyle" | "borderConfig">) => {
  return {
    columnStyle: {
      backgroundColor: ospThemes.colors.background_solid_secondary,
      borderBottom: `${options.borderConfig?.borderWidth}px solid ${options.borderConfig?.borderColor}`,
      ...options.columnStyle,
    },
    cellStyle: {
      backgroundColor: ospThemes.colors.background_solid_secondary,
      color: ospThemes.colors.content_tertiary,
      ...options.cellStyle,
    },
    cellBorderTop: {
      borderTop: `${options.borderConfig?.borderWidth}px solid ${options.borderConfig?.borderColor}`,
    },
    tbodyStyle: {
      ...options.spaceStyle,
    } as React.CSSProperties,
  }
}